import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, map, of, Subscription, switchMap, take } from 'rxjs';
import { Option } from '../../../../../core/models/option.model';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomValidatorService } from '../../../../../components/form-elements/custom-validator.service';
import { ContactOurExpertsService } from '../_core/contact-our-experts.service';
import { ToasterService } from '../../../../../components/toaster/toaster.service';
import { TOASTER_TYPE } from '../../../../../components/toaster/toaster.model';
import { ContactForm, SUBTOPIC_ACTIONS, SubTopicRequest } from '../_core/contact-our-experts.model';
import { DisplaySubContentComponent } from './display-sub-content/display-sub-content.component';
import { UserAuxService } from '../../../user/core/user-aux.service';
import { Country, I18nModule, UserAddressService, WindowRef } from '@spartacus/core';
import { LoaderService } from '../../../../../components/loader/loader.service';
import { LOADER_TYPE } from '../../../../../components/loader/loader.model';
import { isEmpty } from '../../../../../core/services/simple-tools';
import { ModalService } from '../../../../../components/modal-list/modal-list.service';
import { OptionsRequest } from '../../../../../core/models/others.model';
import { initialStatus, loadingStatus } from '../../../../../core/models/request-status.model';
import { AlertComponent } from '../../../../../components/alert/alert.component';
import { LocalLoaderComponent } from '../../../../../components/loader/local-loader/local-loader.component';
import { Alert, ALERT_TYPE } from '../../../../../components/alert/alert.model';
import { RecaptchaService } from '../../../../../core/recaptcha/recaptcha.service';

@Component({
  selector: 'modal-contact-our-expert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DisplaySubContentComponent,
    AlertComponent,
    LocalLoaderComponent,
    I18nModule,
  ],
  templateUrl: './modal-contact-our-expert.component.html',
  styleUrl: './modal-contact-our-expert.component.scss',
})
export class ModalContactOurExpertComponent implements OnInit, OnDestroy {
  SUBTOPIC_ACTIONS = SUBTOPIC_ACTIONS;
  subscription = new Subscription();

  supportTopicRequest$ = new BehaviorSubject<OptionsRequest>(initialStatus);
  supportTopic$ = new BehaviorSubject<Option[]>([]);
  subTopic$ = new BehaviorSubject<SubTopicRequest>({});
  options$ = new BehaviorSubject<SubTopicRequest>({});

  countries$ = new BehaviorSubject<Country[]>([]);
  businessTypes$ = new BehaviorSubject<Option[]>([]);
  stateRegions$ = new BehaviorSubject<Option[]>([]);

  form: FormGroup = this.fb.group({
    supportTopic: ['', [this.customValidatorService.required()]],
    subTopic: ['', [this.customValidatorService.required()]],
  });

  formContact: FormGroup = this.fb.group({
    firstName: ['', [this.customValidatorService.required()]],
    lastName: ['', [this.customValidatorService.required()]],
    email: ['', [this.customValidatorService.isEmail()]],
    phoneNumber: ['', [this.customValidatorService.required()]],
    extension: ['', []],
    companyName: ['', [this.customValidatorService.required()]],
    businessType: ['', [this.customValidatorService.required()]],
    country: ['', [this.customValidatorService.required()]],
    region: ['', [this.customValidatorService.required()]],
    city: ['', [this.customValidatorService.required()]],
    zipCode: ['', [this.customValidatorService.required()]],
    info: ['', [this.customValidatorService.required()]],
    optIn: [false, []],
    agreeWithPolicy: [false, [this.customValidatorService.required(), Validators.requiredTrue]],
  });

  alertErrorMessageTopic: Alert = {
    isActive: true,
    title: '',
    content: '',
    containerClass: '',
    hasCloseButton: false,
    type: ALERT_TYPE.DANGER,
  };

  constructor(
    private fb: FormBuilder,
    private customValidatorService: CustomValidatorService,
    private contactOurExpertsService: ContactOurExpertsService,
    private toasterService: ToasterService,
    private userAuxService: UserAuxService,
    private userAddressService: UserAddressService,
    private loaderService: LoaderService,
    private modalService: ModalService,
    private winref: WindowRef,
    private recaptchaService: RecaptchaService,
  ) {}

  ngOnInit(): void {
    this.fetchSupportTopics();
    this.fetchBusinessTypes();
    this.fetchCountries();

    this.listnerCountryChange();
    this.listnerSubTopicChange();
    // this.fakePopulate();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fakePopulate() {
    this.formContact.patchValue({
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phoneNumber: '1234567890',
      extension: '123',
      companyName: 'Example Inc.',
      businessType: 'businessType1',
      country: 'US',
      region: 'CA',
      city: 'Los Angeles',
      zipCode: '90001',
      info: 'This is a test message.',
      optIn: true,
      agreeWithPolicy: true,
    });
  }

  fetchSupportTopics(): void {
    this.supportTopicRequest$.next(loadingStatus);

    this.contactOurExpertsService.getSupportTopics().subscribe(reponse => {
      this.supportTopicRequest$.next(reponse);

      if (reponse.hasError) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: reponse.message!,
        });

        this.alertErrorMessageTopic.content = reponse.message!;
        return;
      }

      this.supportTopic$.next(reponse.options || []);
    });
  }

  getSubTopics(supportTopic: Option): void {
    this.form.get('subTopic')?.reset();

    this.loaderService.init({
      type: LOADER_TYPE.INFINITE,
      status: true,
    });

    this.contactOurExpertsService.getSubTopics(supportTopic.code).subscribe(reponse => {
      this.loaderService.init({
        type: LOADER_TYPE.INFINITE,
        status: false,
      });

      if (reponse.hasError) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: reponse.message!,
        });

        return;
      }

      this.subTopic$.next(reponse);
    });
  }

  fetchBusinessTypes() {
    this.userAuxService
      .getBusinessTypes()
      .pipe(take(1))
      .subscribe(response => {
        if (response.hasError) {
          this.toasterService.init({
            type: TOASTER_TYPE.DANGER,
            content: response.message!,
          });
        }

        this.businessTypes$.next(response.options || []);
      });
  }

  fetchCountries(): void {
    this.userAddressService.getDeliveryCountries().subscribe(countries => {
      if (countries && countries.length) {
        const us_country = countries.find(country => country.isocode === 'US');
        const ca_country = countries.find(country => country.isocode === 'CA');
        const otherCountries = countries.filter(country => country.isocode !== 'US' && country.isocode !== 'CA');
        const sortedCountries: Country[] = [];
        if (us_country) sortedCountries.push(us_country);
        if (ca_country) sortedCountries.push(ca_country);
        this.countries$.next([...sortedCountries, ...otherCountries]);
      } else {
        this.countries$.next(countries);
      }
    });

    this.userAddressService.loadDeliveryCountries();
  }

  getOption(subTopic: Option): void {
    this.loaderService.init({
      type: LOADER_TYPE.INFINITE,
      status: true,
    });

    this.contactOurExpertsService.getOption(subTopic.code).subscribe(reponse => {
      this.loaderService.init({
        type: LOADER_TYPE.INFINITE,
        status: false,
      });

      if (reponse.hasError) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: reponse.message!,
        });

        return;
      }

      setTimeout(() => {
        if (this.winref.isBrowser()) {
          this.winref.document.querySelector('.js-contact-us')?.addEventListener('click', () => {
            const options = this.options$.getValue();

            if (options.subTopic) {
              options.subTopic.action = SUBTOPIC_ACTIONS.SHOW_FORM;
            }

            this.options$.next(options);
          });
        }
      }, 500);

      this.options$.next(reponse);
    });
  }

  listnerCountryChange() {
    this.subscription.add(
      this.countryObservable(this.formContact.get('country'))?.subscribe(response => {
        this.stateRegions$.next(response.options || []);
      }),
    );
  }

  listnerSubTopicChange() {
    this.subscription.add(
      this.form.get('subTopic')?.valueChanges.subscribe(subTopicCode => {
        if (subTopicCode === 'REQUEST_PART') {
          this.formContact.addControl('modelNumber', this.fb.control('', [this.customValidatorService.required()]));
        } else {
          this.formContact.removeControl('modelNumber');
        }
      })
    );
  }

  countryObservable(control: AbstractControl<any, any> | null | undefined) {
    return control?.valueChanges.pipe(
      filter(value => !isEmpty(value)),
      distinctUntilChanged(),
      switchMap((isoCode: string) => this.userAuxService.getStateRegions(isoCode)),
      map(optionRequest => {
        if (optionRequest.hasError) {
          this.toasterService.init({
            type: TOASTER_TYPE.DANGER,
            content: optionRequest.message!,
          });
        }

        return optionRequest;
      }),
    );
  }

  submit() {
    this.formContact.markAllAsTouched();

    if (this.formContact.valid) {
      const contactForm: ContactForm = {
        topic: this.form.get('supportTopic')?.value,
        subTopic: this.form.get('subTopic')?.value,
        firstName: this.formContact.get('firstName')?.value,
        lastName: this.formContact.get('lastName')?.value,
        email: this.formContact.get('email')?.value,
        phoneNumber: this.formContact.get('phoneNumber')?.value,
        extension: this.formContact.get('extension')?.value,
        companyName: this.formContact.get('companyName')?.value,
        businessType: this.formContact.get('businessType')?.value,
        country: this.formContact.get('country')?.value,
        region: this.formContact.get('region')?.value,
        city: this.formContact.get('city')?.value,
        zipCode: this.formContact.get('zipCode')?.value,
        modelNumber: this.formContact.get('modelNumber')?.value,
        info: this.formContact.get('info')?.value,
        optIn: this.formContact.get('optIn')?.value,
        agreeWithPolicy: this.formContact.get('agreeWithPolicy')?.value,
      };

      this.recaptchaService
        .getToken('register')
        .pipe(
          switchMap(response => {
            if (response.hasError || isEmpty(response.token)) {
              this.toasterService.init({
                type: TOASTER_TYPE.DANGER,
                content: response.message!,
              });

              return of(response);
            }

            contactForm.token = response.token!;
            return this.contactOurExpertsService.submitContactForm(contactForm);
          }),
        )
        .subscribe(response => {
          this.toasterService.init({
            type: response.hasError ? TOASTER_TYPE.DANGER : TOASTER_TYPE.SUCCESS,
            content: response.message!,
          });

          if (response.hasSuccess) {
            this.modalService.close();
          }
        });
    }
  }
}